import { useEffect, useState, useSyncExternalStore } from "react";

import { sourcesForEntity } from "./dataDelay.mjs";
import { getSupabase, isSupabaseConfigured } from "./supabase";
import {
  ANY_ENTITY,
  INCIDENT_SNAPSHOT,
  combineSourceStamp,
  formatUpdatedLabel,
  parseTimestamp,
  stampFromRows,
  updatedAtText,
  updatedAtTitle,
  type UpdatedAtStamp,
} from "./tabFreshness.mjs";
import { slugForTenantId } from "./tenant";
import { scopeTenant } from "./tenantScope.mjs";

/**
 * AJ 2026-09-30: the "Updated @ <time>" read. One read per (tenant, entity)
 * per page load, cached here in memory, shared by every consumer: the tab's
 * own chip and the sidebar freshness badge read the same promise, so the two
 * always print the same instant.
 *
 * THE READ. sync_log: `entity_key, completed_at where tenant_id = ... and
 * entity_key = ... and status = 'success' order by completed_at desc limit 1`
 * (idx_sync_log_tenant_entity; RLS 0015/0044 lets a tenant member read their
 * own tenant's rows, and scopeTenant binds the tenant so a staff session does
 * not blend tenants). Incidents: the newest safety_incident.snapshot_taken_on.
 *
 * NEW DATA, NOT A RE-PUSH (2026-10-05). For an entity fed by source files the
 * stamp is the earlier of that sync and the newest loaded file:
 * `uploads.processed_at where customer_id = <slug> and status = 'processed'
 * and detected_source in (<the entity's sources>) order by processed_at desc
 * limit 1` (combineSourceStamp). public.uploads is keyed by the tenant's slug;
 * a failed read of it leaves the sync stamp standing, as before.
 *
 * NEVER BLOCKS. The hook returns null until the answer arrives and on any
 * failure; the chip simply is not there. A failed read is dropped from the
 * cache so the next mount tries again; a success (or a clean "no rows") is
 * kept for the page load.
 */

const READ_TIMEOUT_MS = 8000;

const cache = new Map<string, Promise<UpdatedAtStamp | null>>();

/**
 * Bumped when a live report publishes a newer stamp (publishTabUpdatedAt):
 * every useTabUpdatedAtState consumer (the sidebar badge) re-reads the cache,
 * so the badge follows the same signal as the tab's chip.
 */
let published = 0;
const listeners = new Set<() => void>();
function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
const getPublished = (): number => published;

/** Test / tenant-switch hook: forget every cached stamp. */
export function __resetTabUpdatedAtCache(): void {
  cache.clear();
}

/**
 * A live report took a newer sync (its data is already swapped in): make
 * `stamp` the cached answer for (tenant, entity) and tell every consumer.
 */
export function publishTabUpdatedAt(
  tenantId: string,
  entityKey: string,
  stamp: UpdatedAtStamp,
): void {
  cache.set(`${tenantId}|${entityKey}`, Promise.resolve(stamp));
  published += 1;
  for (const l of [...listeners]) l();
}

type Rows = Record<string, unknown>[] | null;
type Result = { data: Rows; error: { message?: string } | null };

async function read(tenantId: string, entityKey: string): Promise<UpdatedAtStamp | null> {
  const supabase = getSupabase();
  const signal = AbortSignal.timeout(READ_TIMEOUT_MS);
  let result: Result;
  if (entityKey === INCIDENT_SNAPSHOT) {
    result = (await scopeTenant(
      supabase.from("safety_incident").select("snapshot_taken_on"),
      tenantId,
    )
      .not("snapshot_taken_on", "is", null)
      .order("snapshot_taken_on", { ascending: false })
      .limit(1)
      .abortSignal(signal)) as Result;
  } else {
    let query = scopeTenant(
      supabase.from("sync_log").select("entity_key, completed_at, status"),
      tenantId,
    )
      .eq("status", "success")
      .not("completed_at", "is", null);
    if (entityKey !== ANY_ENTITY) query = query.eq("entity_key", entityKey);
    result = (await query
      .order("completed_at", { ascending: false })
      .limit(1)
      .abortSignal(signal)) as Result;
  }
  if (result.error) throw new Error(result.error.message ?? "read failed");
  const stamp = stampFromRows(entityKey, result.data);
  if (!stamp || entityKey === INCIDENT_SNAPSHOT || entityKey === ANY_ENTITY) return stamp;
  return combineSourceStamp(stamp, await newestSourceFile(tenantId, entityKey));
}

/**
 * The newest processed source file for the entity, or null (no source feeds
 * it, the slug is not resolved here, none was ever loaded, or the read
 * failed). Never throws.
 */
async function newestSourceFile(tenantId: string, entityKey: string): Promise<Date | null> {
  const sources = sourcesForEntity(entityKey);
  const slug = slugForTenantId(tenantId);
  if (sources.length === 0 || !slug) return null;
  try {
    // public.uploads carries the slug (customer_id), not tenant_id; the
    // customer_id filter is this read's tenant binding.
    const result = (await getSupabase()
      .from("uploads")
      .select("processed_at")
      .eq("customer_id", slug)
      .eq("status", "processed")
      .in("detected_source", sources)
      .not("processed_at", "is", null)
      .order("processed_at", { ascending: false })
      .limit(1)
      .abortSignal(AbortSignal.timeout(READ_TIMEOUT_MS))) as Result;
    if (result.error) return null;
    return parseTimestamp(result.data?.[0]?.processed_at) ?? null;
  } catch {
    return null;
  }
}

/**
 * Bumped whenever a live report publishes a newer stamp: a consumer that
 * reads several stamps through fetchTabUpdatedAt (the Data delayed banner)
 * re-reads on it, as the sidebar badge does.
 */
export function useTabStampVersion(): number {
  return useSyncExternalStore(subscribe, getPublished, getPublished);
}

/**
 * One fresh, uncached read (the live poll): one request, tenant-scoped,
 * status success, order by completed_at desc, limit 1. Rejects on failure.
 */
export function readTabUpdatedAt(
  tenantId: string,
  entityKey: string,
): Promise<UpdatedAtStamp | null> {
  return read(tenantId, entityKey);
}

/** The cached stamp for (tenant, entity). Rejections are not cached. */
export function fetchTabUpdatedAt(
  tenantId: string,
  entityKey: string,
): Promise<UpdatedAtStamp | null> {
  const key = `${tenantId}|${entityKey}`;
  let pending = cache.get(key);
  if (!pending) {
    pending = read(tenantId, entityKey).catch((err: unknown) => {
      cache.delete(key);
      console.warn(
        `updated-at unavailable for ${entityKey}: ${err instanceof Error ? err.message : String(err)}`,
      );
      return null;
    });
    cache.set(key, pending);
  }
  return pending;
}

export interface TabUpdatedAt extends UpdatedAtStamp {
  /** The time part: "4:52 PM", "Sep 29, 4:52 PM", or "Sep 28" for a date. */
  label: string;
  /** The chip text: "Updated @ 4:52 PM". */
  text: string;
  /** The hover title: full timestamp + entity name. */
  title: string;
}

/**
 * The raw stamp and whether the read is still out, for consumers that format
 * it themselves (the sidebar badge). `pending` is false once the read has
 * answered (with a stamp or with nothing) and whenever there is nothing to read.
 */
export function useTabUpdatedAtState(
  tenantId: string | null | undefined,
  entityKey: string | null | undefined,
): { pending: boolean; stamp: UpdatedAtStamp | null } {
  const key = tenantId && entityKey && isSupabaseConfigured() ? `${tenantId}|${entityKey}` : null;
  const [state, setState] = useState<{ key: string | null; stamp: UpdatedAtStamp | null }>({
    key: null,
    stamp: null,
  });
  const version = useSyncExternalStore(subscribe, getPublished, getPublished);
  useEffect(() => {
    if (!key || !tenantId || !entityKey) return;
    let cancelled = false;
    void fetchTabUpdatedAt(tenantId, entityKey).then((stamp) => {
      if (!cancelled) setState({ key, stamp });
    });
    return () => {
      cancelled = true;
    };
  }, [key, tenantId, entityKey, version]);
  if (!key) return { pending: false, stamp: null };
  // A tenant or tab change must not keep showing the previous stamp.
  return state.key === key
    ? { pending: false, stamp: state.stamp }
    : { pending: true, stamp: null };
}

/**
 * The tab's "Updated @" stamp, or null while pending, when the entity has no
 * successful sync, when entityKey is null (a tab with no feed), or on error.
 */
export function useTabUpdatedAt(
  tenantId: string | null | undefined,
  entityKey: string | null | undefined,
): TabUpdatedAt | null {
  const { stamp } = useTabUpdatedAtState(tenantId, entityKey);
  if (!stamp) return null;
  const label = formatUpdatedLabel(stamp.at, { dateOnly: stamp.dateOnly });
  return {
    ...stamp,
    label,
    text: updatedAtText(label),
    title: updatedAtTitle(stamp.at, stamp.entity, { dateOnly: stamp.dateOnly }),
  };
}
